import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { KAL_DIALOG_DATA, KalDialogRef } from '@kalidea/kaligraphi';
import { ArticleMagazineModel, MutationLikeArticleMagazineArgs, } from 'src/graphql/generated-types';
import { throwError } from 'rxjs';
import { transition, trigger } from '@angular/animations';
import { slideFromRight, slideToRight, zoomIn, zoomOut } from 'src/app/_core/tools/animations';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import gql from 'graphql-tag';
import { SharedMagazineSetMagazineAction } from 'src/app/_shared/stores/shared-magazine/shared-magazine.actions';
import {
    MagazineActionEnum,
    MagazineActionType,
    MagazineService
} from 'src/app/_shared/services/magazine/magazine.service';

export enum SharedMagazineDisplayMode {
    Large = 'LARGE',
    Small = 'SMALL'
}

export enum SharedMagazinePictureFormat {
    Full = 'full',
    Thumbnail = 'thumbnail'
}

export class SharedMagazineDialogData {
    magazine: ArticleMagazineModel = null;
}

@Component({
    selector: 'app-shared-magazine-dialog',
    templateUrl: './shared-magazine-dialog.component.html',
    styleUrls: ['./shared-magazine-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slide', [
            transition('* => large', slideFromRight),
            transition('* => small', zoomIn),
            transition('small => close', zoomOut),
            transition('large => close', slideToRight),
        ])
    ],
    standalone: false
})
export class SharedMagazineDialogComponent extends BaseComponent implements OnInit {

    // state for animations
    state: 'large' | 'small' | 'close';

    picture = null;

    constructor(private readonly dialogRef: KalDialogRef<SharedMagazineDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: SharedMagazineDialogData,
                private readonly layoutService: LayoutService,
                protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly magazineService: MagazineService) {
        super(cdr);
    }

    get isLarge(): boolean {
        return this.magazine.Mode === SharedMagazineDisplayMode.Large;
    }

    get magazine(): ArticleMagazineModel {
        return this.data.magazine;
    }

    share(target: 'article' | 'email'): void {
        const magazineAction = (target === 'article'
                ? MagazineActionEnum.SharePortal
                : MagazineActionEnum.ShareMail
        ) as MagazineActionType;

        this.watch$ = this.magazineService.magazineAction(this.magazine, magazineAction).pipe(
            switchMap(() => this.dispatch$(new SharedMagazineSetMagazineAction(this.magazine))),
            tap(() => {
                this.close();

                if (target === 'article') {
                    this.layoutService.navigate({
                        content: ['web-administration', 'editorial', 'list'],
                        focus: ['web-administration', 'editorial', 'article', 'create']
                    }, 'content');
                } else {
                    this.layoutService.navigate({
                        content: ['contact', 'contact', 'mail'],
                        focus: 'empty'
                    }, 'content');
                }
            })
        );
    }

    close(): void {
        this.state = 'close';
    }

    animationDone($event): void {
        // close dialog when animation going to 'close' state
        if ($event.toState === 'close') {
            this.dialogRef.close();
        }
    }

    likeMagazine(): void {
        if (this.loading) {
            return;
        }

        let mutation = gql`
            mutation LikeArticleMagazine(
                $contexte: ContextInfosInput,
                $idArticle: String
            ) {
                LikeArticleMagazine(
                    contexte: $contexte,
                    idArticle: $idArticle
                )
            }`;

        if (!this.magazine.CanLike) {
            mutation = gql`
                mutation UnlikeArticleMagazine(
                    $contexte: ContextInfosInput,
                    $idArticle: String
                ) {
                    UnlikeArticleMagazine(
                        contexte: $contexte,
                        idArticle: $idArticle
                    )
                }`;
        }

        this.loading = true;

        const magazineAction = (!this.magazine.CanLike
                ? MagazineActionEnum.Unlike
                : MagazineActionEnum.Like
        ) as MagazineActionType;

        type type = 'LikeArticleMagazine' | 'UnlikeArticleMagazine';
        this.watch$ = this.graphqlService.mutateOne<type, MutationLikeArticleMagazineArgs>({
            mutation,
            variables: {
                idArticle: this.magazine.Id
            }
        }).pipe(
            tap(() => {
                this.loading = false;
                this.magazine.NombreLikes += this.magazine.CanLike ? 1 : -1;
                this.magazine.CanLike = !this.magazine.CanLike;
            }),
            switchMap(() => this.magazineService.magazineAction(this.magazine, magazineAction)),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private getPicture(): void {
        const pictureSize = this.isLarge ? SharedMagazinePictureFormat.Full : SharedMagazinePictureFormat.Thumbnail;
        const picture = this.magazine?.ListeFormatImageAttachee.KeyValueOfstringstring.find(({Key}) => Key === pictureSize)?.Value;

        if (picture) {
            this.picture = picture;
            return;
        }

        this.picture = this.magazine?.ListeFormatImageMiseEnAvant.KeyValueOfstringstring.find(({Key}) => Key === pictureSize)?.Value;
    }

    ngOnInit(): void {
        this.state = this.isLarge ? 'large' : 'small';
        this.getPicture();
    }

}
